import { useEffect, useState } from 'react';
import { ComponentConfig, FieldLabel } from '@measured/puck';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../services/constants';
import CustomTooltip from '../../../components/common/tooltip';
import {
  getBorderProps,
  getcurrentDeviceType,
  getNewMarginStyles,
  getPaddingStyles,
} from '../../utils/helper';
import {
  paddingFields,
  paddingProps,
  PaddingProps,
  PaddingType,
} from '../../puck/reusable-props/padding';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import { toolTipInfo } from '../../utils/constants';
import {
  marginFields,
  marginProps,
  MarginProps,
  MarginType,
} from '../../puck/reusable-props/NewMargin';
import QuilHelper from '../../puck/reusable-props/quilHelper';
import WithAccordion from '../customComponents/CustomAccordion';
import ColorPicker from '../../puck/reusable-props/ColorPicker';
import {
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  Tooltip,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Select from '../../../components/common/select';
import Toggle from '../../../components/common/toggleSwitchCustom';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';
import NewMarginFiled from '../../puck/reusable-props/newMarginField';
import PaddingComponent from '../customComponents/PaddingComponent';
import { setIsEmailConsent } from '../../redux/slices/commonSlice';

export type EmailConsentProps = {
  border: IBorderValueProps;
  checkboxText: string;
  defaultChecked: boolean;
  legalText: {
    text: string;
    margin: MarginType;
    padding: PaddingType;
    border: IBorderValueProps;
  };
  customCheckbox?: {
    checkboxColor?: string;
    checkedBorderColor?: string;
    uncheckedBorderColor?: string;
    checkboxshape?: string;
    checkboxSize?: string;
    enrollmentStyle?: string;
    checkMarkColor?: string;
    checkedToggleColor?: string;
    uncheckedToggleColor?: string;
    toggleSize?: string;
    checkMarkSize?: string;
  };
  background: {
    color: string;
    isInherit: boolean;
  };
} & MarginProps &
  PaddingProps;

export const EmailConsent = ({
  useAppSelector,
  useAppDispatch,
}: ReduxDispatchProps &
  ReduxSelectorProps): ComponentConfig<EmailConsentProps> => {
  return {
    label: (
      <CustomTooltip title="Email Consent">
        <span>Email Consent</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      checkboxText: {
        type: 'custom',
        label: 'Checkbox Text',
        render: ({ value, onChange }) => (
          <WithAccordion title="Checkbox Text">
            <QuilHelper value={value} onChange={onChange} id={1} />
          </WithAccordion>
        ),
      },
      legalText: {
        type: 'custom',
        label: 'Legal Text',
        render: ({ value, onChange }) => (
          <WithAccordion title="Legal Text">
            <QuilHelper
              value={value.text}
              onChange={text => onChange({ ...value, text })}
              id={2}
            />
            <NewMarginFiled
              value={value.margin}
              onChange={margin => onChange({ ...value, margin })}
              title="Margin"
            />
            <PaddingComponent
              value={value.padding}
              onChange={padding => onChange({ ...value, padding })}
              title="Padding"
            />
            <Border
              value={value.border}
              onChange={border => onChange({ ...value, border })}
              title="Border"
            />
          </WithAccordion>
        ),
      },
      customCheckbox: {
        type: 'custom',
        label: 'Custom Checkbox',
        render: ({ value, onChange }) => {
          const options = [
            { label: 'Square', value: 'rounded' },
            { label: 'Circle', value: 'rounded-full' },
          ];
          return (
            <WithAccordion title="Custom Checkbox">
              <>
                <div>
                  <ColorPicker
                    value={value?.checkboxColor || '#000000'}
                    onChange={color =>
                      onChange({ ...(value || {}), checkboxColor: color })
                    }
                    label="Checkbox Color"
                    tooltip="Checkbox Color"
                  />
                </div>
                <div>
                  <ColorPicker
                    value={value?.checkedBorderColor}
                    onChange={color =>
                      onChange({ ...(value || {}), checkedBorderColor: color })
                    }
                    label="Checked Border Color"
                    tooltip="Checked Border Color"
                  />
                </div>
                <div>
                  <ColorPicker
                    value={value?.uncheckedBorderColor}
                    onChange={color =>
                      onChange({
                        ...(value || {}),
                        uncheckedBorderColor: color,
                      })
                    }
                    label="Unchecked Border Color"
                    tooltip="Unchecked Border Color"
                  />
                </div>
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip
                          title={'Checkbox Shape' as keyof typeof toolTipInfo}>
                          <span>Checkbox Shape</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                    className="w-full">
                    <div className="w-full p-2">
                      <ToggleButtonGroup
                        value={value?.checkboxshape}
                        exclusive
                        className="w-full"
                        onChange={(_e, selectValue) => {
                          selectValue &&
                            onChange({ ...value, checkboxshape: selectValue });
                        }}>
                        {options.map((option, index: number) => (
                          <ToggleButton
                            key={index}
                            size="small"
                            value={option.value}
                            disableRipple
                            aria-label={option.value}
                            className="flex-1">
                            {option.label}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </div>
                  </FieldLabel>
                </div>
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip
                          title={'Checkbox Size' as keyof typeof toolTipInfo}>
                          <span>Checkbox Size</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                    className="w-full">
                    <input
                      type="number"
                      value={value?.checkboxSize || 16}
                      onChange={e =>
                        onChange({
                          ...value,
                          checkboxSize: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      placeholder="Enter checkbox size"
                    />
                  </FieldLabel>
                </div>
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip title="EnrollementStyle">
                          <span>Enrollement Style</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                  />
                  <Select
                    value={value?.enrollmentStyle}
                    onChange={val =>
                      onChange({ ...value, enrollmentStyle: val })
                    }
                    options={[
                      {
                        label: 'Checkbox',
                        value: 'checkbox',
                      },
                      {
                        label: 'Toggle',
                        value: 'toggle',
                      },
                    ]}
                  />
                </div>
                <ColorPicker
                  value={value?.checkMarkColor || '#ffffff'}
                  onChange={color =>
                    onChange({
                      ...(value || {}),
                      checkMarkColor: color,
                    })
                  }
                  label={'Check Mark Color'}
                  tooltip={'Check Mark Color'}
                />
                <ColorPicker
                  value={value?.checkedToggleColor || '#4f46e5'}
                  onChange={color =>
                    onChange({
                      ...(value || {}),
                      checkedToggleColor: color,
                    })
                  }
                  label={'Checked Toggle Color'}
                  tooltip={'Checked Toggle Color'}
                />
                <ColorPicker
                  value={value?.uncheckedToggleColor || '#d1d5db'}
                  onChange={color =>
                    onChange({
                      ...(value || {}),
                      uncheckedToggleColor: color,
                    })
                  }
                  label={'Unchecked Toggle Color'}
                  tooltip={'Unchecked Toggle Color'}
                />
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip
                          title={'Toggle Size' as keyof typeof toolTipInfo}>
                          <span>Toggle Size</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                    className="w-full">
                    <input
                      type="number"
                      value={value?.toggleSize || 20}
                      onChange={e =>
                        onChange({
                          ...value,
                          toggleSize: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      placeholder="Enter toggle size"
                    />
                  </FieldLabel>
                </div>
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip
                          title={'Check Mark Size' as keyof typeof toolTipInfo}>
                          <span>Check Mark Size</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                    className="w-full">
                    <input
                      type="number"
                      value={value?.checkMarkSize || 16}
                      onChange={e =>
                        onChange({
                          ...value,
                          checkMarkSize: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      placeholder="Enter toggle size"
                    />
                  </FieldLabel>
                </div>
              </>
            </WithAccordion>
          );
        },
      },
      defaultChecked: {
        type: 'custom',
        label: 'Default Checked',
        render: ({ value, onChange }) => (
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm font-semibold">Default Checked</p>
            <div className="flex gap-0.5 items-center justify-between">
              <Tooltip
                title={
                  'To toggle the checkbox/toggle button, it is set to default checked when the webpage loads.'
                }
                placement="right-end">
                <InfoIcon fontSize="small" className="h-1 w-1" />
              </Tooltip>
              <Switch
                checked={value}
                onChange={e => onChange(e.target.checked)}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            </div>
          </div>
        ),
      },
      ...backgroungFields,
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        label: 'Border',
        render: ({ value, onChange }) => (
          <Border
            value={value}
            onChange={value => onChange({ ...value })}
            title="Border"
          />
        ),
      },
    },
    defaultProps: {
      defaultChecked: false,
      checkboxText:
        '<p style="line-height: 1.5; font-size: 16px;">Email me with news and offers</p>',
      legalText: {
        text: '<p style="line-height: 1.5; font-size: 14px;">By signing up via email, you agree to receive recurring automated marketing messages, including cart reminders, at the email address provided. Consent is not a condition of purchase. Click "Unsubscribe" from the email message to be removed from further messaging.</p>',
        margin: marginProps.margin,
        padding: paddingProps.padding,
        border: borderProps.border,
      },
      customCheckbox: {
        checkboxColor: '#4f46e5',
        checkedBorderColor: '#4f46e5',
        uncheckedBorderColor: '#d1d5db',
        checkboxshape: 'rounded',
        checkboxSize: '16',
        enrollmentStyle: 'checkbox',
        checkMarkColor: '#ffffff',
        checkedToggleColor: '#4f46e5',
        uncheckedToggleColor: '#d1d5db',
        toggleSize: '20',
        checkMarkSize: '16',
      },
      background: {
        color: '#ffffff',
        isInherit: false,
      },
      margin: marginProps.margin,
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      ...borderProps,
    },
    render: ({
      margin,
      padding,
      editMode,
      border,
      checkboxText,
      legalText,
      customCheckbox,
      background,
      defaultChecked,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getNewMarginStyles(margin, currentDeviceType);
      const paddingStyles = getPaddingStyles(padding, currentDeviceType);
      const [check, setCheck] = useState(false);
      const dispatch = useAppDispatch();

      useEffect(() => {
        setCheck(defaultChecked);
        dispatch(setIsEmailConsent(defaultChecked));
      }, [defaultChecked, dispatch]);

      const handleCheckChange = (newCheckState: boolean) => {
        setCheck(newCheckState);
        dispatch(setIsEmailConsent(newCheckState));
      };

      return (
        <div
          className={`${border?.borderStyle?.[currentDeviceType]}`}
          style={{
            backgroundColor: background?.isInherit
              ? 'inherit'
              : background?.color,
            ...marginStyles,
            ...paddingStyles,
            ...getBorderProps(border, currentDeviceType),
          }}>
          <div className="flex items-center">
            {customCheckbox?.enrollmentStyle === 'checkbox' ? (
              <input
                type="checkbox"
                onChange={() => handleCheckChange(!check)}
                checked={check}
                className={`${customCheckbox?.checkboxshape} check-box-billing focus:ring-0 focus:ring-offset-0 h-4 w-4 border-gray-300 cursor-pointer`}
                style={{
                  color: customCheckbox?.checkboxColor,
                  width: `${customCheckbox?.checkboxSize}px`,
                  height: `${customCheckbox?.checkboxSize}px`,
                  borderColor: check
                    ? customCheckbox?.checkedBorderColor
                    : customCheckbox?.uncheckedBorderColor,
                }}
              />
            ) : (
              <Toggle
                checked={check}
                style={{
                  backgroundColor: check
                    ? customCheckbox?.checkedToggleColor
                    : customCheckbox?.uncheckedToggleColor,
                  color: customCheckbox?.checkMarkColor,
                  width: `${customCheckbox?.toggleSize}px`,
                  height: `${customCheckbox?.toggleSize}px`,
                }}
                circleWidth={`${customCheckbox?.checkMarkSize}px`}
                circleHeight={`${customCheckbox?.checkMarkSize}px`}
                handleToggle={() => {
                  handleCheckChange(!check);
                }}
              />
            )}
            <div
              className="text-editor-content ml-2 w-full"
              dangerouslySetInnerHTML={{ __html: checkboxText }}
            />
          </div>

          {/* Always render this container */}
          <div
            className={`overflow-hidden transition-[max-height,opacity] duration-700 ease-in-out ${
              check ? 'max-h-max opacity-100' : 'max-h-0 opacity-0'
            }`}>
            {check && (
              <div
                className={`text-editor-content ${legalText.border?.borderStyle?.[currentDeviceType]}`}
                style={{
                  ...getNewMarginStyles(legalText.margin, currentDeviceType),
                  ...getPaddingStyles(legalText.padding, currentDeviceType),
                  ...getBorderProps(legalText.border, currentDeviceType),
                }}
                dangerouslySetInnerHTML={{ __html: legalText.text }}
              />
            )}
          </div>
        </div>
      );
    },
  };
};
